import { useState, useMemo } from 'react'

type UsePaginationParams = {
  /** Page selected on initial render, defaults to 1 */
  initialPage?: number

  /** Page selected on initial render, defaults to 1 */
  limit?: number

  /** Total amount of pages */
  total?: number

  onChange?: (pagination: Pagination) => void
}

type UsePaginationResult = {
  page: number
  offset: number
  total: number
  isHasMorePages: boolean
  setPage: (page: number) => void
  next: () => void
  previous: () => void
  first: () => void
  last: () => void
}

type Pagination = {
  page: number
  offset: number
  total: number
}

export const usePagination = ({
  initialPage = 1,
  limit = 10,
  total = 0,
  onChange,
}: UsePaginationParams): UsePaginationResult => {
  const [activePage, setActivePage] = useState(initialPage)

  const { lastPage } = useMemo(() => {
    return {
      lastPage: Math.ceil(total / limit),
    }
  }, [limit, total])

  const setPage = (value: number) => {
    let page

    if (value <= 0) {
      page = 1
    } else if (value > lastPage) {
      page = lastPage
    } else {
      page = value
    }

    const offset = getOffset(page)

    setActivePage(page)

    onChange?.({
      page,
      offset,
      total: lastPage,
    })
  }

  const getOffset = (page: number) => {
    if (page === 1) {
      return 0
    } else {
      return (page - 1) * limit
    }
  }

  const next = () => setPage(activePage + 1)
  const previous = () => setPage(activePage - 1)
  const first = () => setPage(1)
  const last = () => setPage(lastPage)

  const isHasMorePages = total > limit

  return {
    page: activePage,
    offset: getOffset(activePage),
    total: lastPage,
    isHasMorePages,
    setPage,
    next,
    previous,
    first,
    last,
  }
}
